// src/features/treinos/components/EvolucaoExercicio.jsx
import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { buscarEvolucaoExercicio } from "../services/workoutLogService.js";
import Style from "../../../styles/features/treinos/EvolucaoExercicio.module.scss";
import GatePremium from "../../../components/premium/GatePremium.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function formatarData(str) {
  if (!str) return "";
  const [, m, d] = str.split("-");
  return `${d}/${m}`;
}

// Chart.js pinta no <canvas>, que NÃO resolve var(--…) nem color-mix(). Então
// lemos o valor concreto da custom property no próprio nó (que herda o tema da
// academia) em runtime e geramos as cores do gráfico a partir dele.
function resolveCssColor(el, varName, fallback) {
  if (!el || typeof window === "undefined") return fallback;
  const v = window.getComputedStyle(el).getPropertyValue(varName).trim();
  return v || fallback;
}

// Aplica alpha a um cor concreta (#RGB | #RRGGBB | rgb()/rgba()). Necessário
// porque o canvas exige rgba() concreto — color-mix não funciona aqui.
function withAlpha(color, alpha) {
  const c = String(color || "").trim();
  let m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  m = /^rgba?\(([^)]+)\)$/i.exec(c);
  if (m) {
    const [r, g, b] = m[1].split(",").map((s) => s.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return c; // formato desconhecido → sem alpha
}

// Opções do seletor "Últimas N sessões".
const N_OPTIONS = [8, 10, 20];

const EvolucaoExercicio = ({ uid, exerciseId, nome, nomeExercicio }) => {
  const [loading, setLoading] = useState(true);
  const [pontos, setPontos] = useState([]);
  const [limite, setLimite] = useState(8); // 0 = todas

  // Cores do tema resolvidas em runtime (acento da academia → var(--gym-accent)).
  const wrapperRef = useRef(null);
  const [themeColors, setThemeColors] = useState({
    accent: "#FBBC04",
    primary: "#1428A0",
  });
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    setThemeColors({
      accent: resolveCssColor(el, "--color-accent", "#FBBC04"),
      primary: resolveCssColor(el, "--color-primary", "#1428A0"),
    });
  }, [loading]); // re-resolve quando o gráfico monta (após o estado de loading)

  const nomeMatch = nome || nomeExercicio;

  useEffect(() => {
    if (!uid || (!exerciseId && !nomeMatch)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Busca tudo; o corte por N é feito no render (troca instantânea).
    buscarEvolucaoExercicio({ uid, exerciseId, nome: nomeMatch })
      .then(setPontos)
      .finally(() => setLoading(false));
  }, [uid, exerciseId, nomeMatch]);

  if (loading) {
    return (
      <div className={Style.loading}>
        <div className="spinner-border spinner-border-sm text-primary" role="status" />
        <span>Carregando evolução...</span>
      </div>
    );
  }

  if (pontos.length === 0) {
    return (
      <div className={Style.emptyState}>
        <span className={Style.emptyIcon}>📈</span>
        <p>Ainda sem registros de carga para este exercício.</p>
        <p className={Style.emptyHint}>
          Registre seus treinos para ver a evolução aqui.
        </p>
      </div>
    );
  }

  // Corte "Últimas N" (mantém as mais recentes).
  const visiveis = limite > 0 ? pontos.slice(-limite) : pontos;
  const labels = visiveis.map((p) => formatarData(p.data));
  const r = visiveis.length === 1 ? 6 : 4;

  const chartData = {
    labels,
    datasets: [
      {
        label: "Carga máx (kg)",
        data: visiveis.map((p) => p.melhorCarga),
        borderColor: themeColors.accent,
        backgroundColor: withAlpha(themeColors.accent, 0.12),
        tension: 0.4,
        pointRadius: r,
        fill: true,
      },
      {
        label: "1RM estimado (kg)",
        data: visiveis.map((p) => parseFloat(p.melhor1RM.toFixed(1))),
        borderColor: "#FBBD04",
        backgroundColor: withAlpha(themeColors.accent, 0.08),
        tension: 0.4,
        pointRadius: r,
        borderDash: [4, 3],
        fill: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "bottom", labels:{ color:"#A3AABA", boxWidth:12, font:{family:"Plus Jakarta Sans"} } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} kg`,
        },
      },
    },
    scales: {
      x:{ticks:{color:"#6B7383"},grid:{display:false}},
      y: {
        ticks: { color:"#6B7383", callback: (v) => `${v} kg` },
        grid:{color:"rgba(163,170,186,.08)"},
      },
    },
  };

  return (
    <GatePremium redesign
      feature="graficosEvolucao"
      contexto={{ origem: "evolucao_exercicio", detalhe: nome || nomeExercicio || exerciseId }}
    >
    <div className={Style.wrapper} ref={wrapperRef}>
      <div className={Style.nSelectRow}>
        <label className={Style.nSelectLabel}>Sessões:</label>
        <select
          className={Style.nSelect}
          value={limite}
          onChange={(e) => setLimite(Number(e.target.value))}
        >
          {N_OPTIONS.map((n) => (
            <option key={n} value={n}>Últimas {n}</option>
          ))}
          <option value={0}>Todas</option>
        </select>
      </div>
      <div className={Style.chartBox}>
        <Line data={chartData} options={options} />
      </div>
    </div>
    </GatePremium>
  );
};

export default EvolucaoExercicio;
